import React, { useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * GracePeriodBanner
 * Rendered at the top of PulseHR whenever the tenant subscription is in its 15-day grace period.
 */
const GracePeriodBanner = ({ subscription, onUpgradeClick }) => {
  const [dismissed, setDismissed] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Check if tenant is currently in grace period
  const isGrace = Boolean(
    subscription?.is_grace ||
    subscription?.status === "GRACE" ||
    (subscription?.grace_days_remaining !== undefined && subscription?.grace_days_remaining !== null && subscription?.grace_days_remaining > 0)
  );

  if (!token || !isGrace || dismissed) {
    return null;
  }

  const daysRemaining = subscription?.grace_days_remaining ?? subscription?.days_remaining ?? 15;
  const isUrgent = daysRemaining <= 3;

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "14px",
        background: isUrgent
          ? "linear-gradient(90deg, #991b1b 0%, #b45309 50%, #d97706 100%)"
          : "linear-gradient(90deg, #b45309 0%, #d97706 50%, #f59e0b 100%)",
        color: "#ffffff",
        boxShadow: "0 4px 14px rgba(180, 83, 9, 0.25)",
        px: { xs: 2, sm: 3 },
        py: 1.5,
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: { xs: "wrap", md: "nowrap" },
        gap: { xs: 1.5, md: 2 }
      }}
    >
        {/* Left: Warning Icon + Descriptive Text */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: 36,
              height: 36,
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              animation: "pulse-warning 2s infinite ease-in-out",
              "@keyframes pulse-warning": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.08)" },
                "100%": { transform: "scale(1)" }
              }
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 22, color: "#ffffff" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "0.88rem", md: "0.93rem" },
                  letterSpacing: "0.2px",
                  lineHeight: 1.2
                }}
              >
                Subscription Ended — 15-Day Grace Period Active
              </Typography>

              <Box
                sx={{
                  bgcolor: isUrgent ? "#dc2626" : "rgba(0, 0, 0, 0.28)",
                  color: "#ffffff",
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  px: 1.2,
                  py: 0.2,
                  borderRadius: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  border: "1px solid rgba(255, 255, 255, 0.3)"
                }}
              >
                {daysRemaining} {daysRemaining === 1 ? "day" : "days"} remaining
              </Box>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.92)",
                fontSize: { xs: "0.78rem", md: "0.82rem" },
                display: "block",
                mt: 0.2,
                fontWeight: 500
              }}
            >
              Zero interruption is currently active for your employees. Please renew or upgrade your plan now before the grace period ends to prevent full application lockout.
            </Typography>
          </Box>
        </Box>

        {/* Right: CTA Button & Dismiss */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: { xs: "100%", md: "auto" },
            justifyContent: { xs: "flex-end", md: "flex-end" }
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={onUpgradeClick}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: "#ffffff",
              color: "#9a3412",
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              borderRadius: "20px",
              px: 2.2,
              py: 0.6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "#fff7ed",
                color: "#7c2d12",
                boxShadow: "0 4px 12px rgba(0,0,0,0.28)",
                transform: "translateY(-1px)"
              },
              transition: "all 0.15s ease-in-out"
            }}
          >
            Renew Now
          </Button>

          <IconButton
            size="small"
            aria-label="dismiss warning banner"
            onClick={() => setDismissed(true)}
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              p: 0.5,
              "&:hover": {
                color: "#ffffff",
                bgcolor: "rgba(255, 255, 255, 0.15)"
              }
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
    </Box>
  );
};

export default GracePeriodBanner;
